import React, { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PlanDetails, PlanTypes } from 'Interfaces/Billing';
import { User } from 'Interfaces/User';
import { selectSubscriptionInfo, selectUser, selectUserPlan } from 'Utils/selectors';

import UIButton from '../UIComponents/UIButton';

import {
  discountOrLess,
  financialReasonPeriodBySubscriptionOption,
} from './common/discountsData';

interface BillingCancelPlanWizardBeforeYouGoProps {
  onCancel?: () => void;
  onSubmit?: () => void;
}

const BillingCancelPlanWizardBeforeYouGo = (
  props: PropsWithChildren<BillingCancelPlanWizardBeforeYouGoProps>,
) => {
  const { children, onCancel, onSubmit } = props;

  const userPlan: PlanDetails = useSelector(selectUserPlan);
  const user: User = useSelector(selectUser);
  const subscriptionInfo = useSelector(selectSubscriptionInfo);

  const isTrial = user.isTrialSubscription;
  const isPersonalPlan = userPlan?.type === PlanTypes.PERSONAL;
  const isBusinessPlan = userPlan?.type === PlanTypes.BUSINESS;

  const period = useMemo(() => {
    return financialReasonPeriodBySubscriptionOption(userPlan?.duration);
  }, [userPlan]);

  const discount = useMemo(() => {
    return discountOrLess(subscriptionInfo.discountPercent, userPlan);
  }, [subscriptionInfo.discountPercent, userPlan]);

  const title = isTrial
    ? "Wait! Don't go just yet."
    : isPersonalPlan && period === 'monthly'
    ? 'Before you go...'
    : isBusinessPlan && period === 'monthly'
    ? 'A Special offer just for you!'
    : '';

  return (
    <>
      <h1 className="billing__plan-cancel-modal-title">{title}</h1>
      {isTrial ? (
        <>
          <p className="billing__plan-cancel-modal-paragraph">
            <b>We&apos;d hate to see you miss out on everything you&apos;ve built.</b>
          </p>
          <p className="billing__plan-cancel-modal-paragraph">
            👉 Enjoy {discount}% off your next 3 months — keep all your Signaturely
            benefits like Unlimited Templates and Custom Branding at half the price.
          </p>
        </>
      ) : period === 'monthly' ? (
        <>
          <p className="billing__plan-cancel-modal-paragraph">
            What if you could keep all your Signaturely perks{' '}
            {isBusinessPlan
              ? 'Unlimited Signature Requests, Unlimited Templates,'
              : '5 Signature Requests, your template,'}{' '}
            and more for half the price for the next 3 months?
          </p>
          <p className="billing__plan-cancel-modal-paragraph">
            You&apos;ll save {discount}% instantly, and nothing changes in how you manage
            your documents.
          </p>
        </>
      ) : null}
      {children}
      <div className="billing__plan-cancel-modal-actions">
        <UIButton
          priority="primary"
          title="Yes, I'm Interested"
          className="billing__plan-cancel-modal-button--primary"
          handleClick={onSubmit}
        />
        <UIButton
          priority="white"
          title="No, thank you"
          className="billing__plan-cancel-modal-button--white"
          handleClick={onCancel}
        />
      </div>
    </>
  );
};

export default BillingCancelPlanWizardBeforeYouGo;
